import React from 'react';
import { parseHTMLToContent, parseRTFToContent, parsedContentToPlainText } from './ClipboardParser';

// ============================================================================
// DEBUG CONTROL: Set to false to disable all debug logging in production
// ============================================================================
const DEBUG_ENABLED = false; // ✅ DISABLED: Rapid typing performance fix

type ClipboardBridgeProps = {
  onPasteText: (text: string) => void;
  onPasteHTML?: (html: string) => void; // reserved for rich-text pipeline (future: styled runs)
  onPasteRTF?: (rtf: string) => void;   // reserved for RTF pipeline (future: styled runs)
  getSelectedText: () => string;
  onCutSelection?: () => void;
};

/**
 * ClipboardBridge
 * Hidden contenteditable surface to capture native clipboard events reliably.
 * 
 * KEY INSIGHT: This component intercepts Ctrl+C/V/X at the window level,
 * focuses itself, and lets the browser fire native clipboard events on it.
 * This is more reliable than navigator.clipboard API.
 */
export const ClipboardBridge = React.forwardRef<HTMLDivElement, ClipboardBridgeProps>(function ClipboardBridge(
  { onPasteText, onPasteHTML, onPasteRTF, getSelectedText, onCutSelection },
  ref
) {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const pendingClipboardAction = React.useRef<'copy' | 'cut' | 'paste' | null>(null);

  React.useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

  const handlePaste = React.useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    DEBUG_ENABLED && console.log('📋 [ClipboardBridge] handlePaste fired');
    try {
      const dt = e.clipboardData;
      if (!dt) {
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] No clipboardData');
        return;
      }

      const html = dt.getData('text/html');
      const rtf = dt.getData('text/rtf');
      const text = dt.getData('text/plain');

      DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Paste data:', {
        hasHTML: !!html,
        htmlLength: html?.length,
        hasRTF: !!rtf,
        hasText: !!text,
        textLength: text?.length,
        textPreview: text?.slice(0, 50)
      });

      // ✅ PHASE 2: Priority-based format handling
      // 1. HTML (from Word, Google Docs, web) - parse to extract paragraphs
      // 2. RTF (legacy Office) - parse to extract paragraphs
      // 3. Plain text (fallback)

      let pastedText: string | null = null;

      // Try HTML first (most common from external sources)
      if (html && html.trim()) {
        try {
          const parsed = parseHTMLToContent(html);
          pastedText = parsedContentToPlainText(parsed);

          DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Parsed HTML:', {
            paragraphCount: parsed.paragraphs.length,
            plainTextLength: pastedText.length,
            preview: pastedText.slice(0, 100)
          });

          // Call onPasteHTML if provided (for future rich-text support)
          if (onPasteHTML) {
            onPasteHTML(html);
          }
        } catch (err) {
          DEBUG_ENABLED && console.log('📋 [ClipboardBridge] HTML parse failed, falling back to plain text:', err);
          pastedText = null;
        }
      }

      // Try RTF if HTML didn't work
      if (!pastedText && rtf && rtf.trim()) {
        try {
          const parsed = parseRTFToContent(rtf);
          pastedText = parsedContentToPlainText(parsed);

          DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Parsed RTF:', {
            paragraphCount: parsed.paragraphs.length,
            plainTextLength: pastedText.length
          });

          // Call onPasteRTF if provided (for future rich-text support)
          if (onPasteRTF) {
            onPasteRTF(rtf);
          }
        } catch (err) {
          DEBUG_ENABLED && console.log('📋 [ClipboardBridge] RTF parse failed, falling back to plain text:', err);
          pastedText = null;
        }
      }

      // Fallback to plain text
      if (!pastedText && text) {
        // Normalize line endings to \r (paragraph break) for MS Word model
        // Windows \r\n → \r, Unix \n → \r, old Mac \r stays \r
        pastedText = text.replace(/\r\n/g, '\r').replace(/\n/g, '\r');
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Using plain text fallback');
      }

      // Call onPasteText with the final text
      if (pastedText) {
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Calling onPasteText with', pastedText.length, 'chars');
        onPasteText(pastedText);
      }

      e.preventDefault();
      if (innerRef.current) innerRef.current.textContent = '';
      pendingClipboardAction.current = null;
    } catch (err) {
      console.error('📋 [ClipboardBridge] Paste error:', err);
    }
  }, [onPasteHTML, onPasteRTF, onPasteText]);

  const handleCopy = React.useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    DEBUG_ENABLED && console.log('📋 [ClipboardBridge] handleCopy fired');
    try {
      const selected = getSelectedText();
      DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Selected text:', { length: selected?.length, preview: selected?.slice(0, 50) });
      
      if (selected && e.clipboardData) {
        e.clipboardData.setData('text/plain', selected);
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Set clipboard data:', selected.length, 'chars');
        e.preventDefault();
      } else {
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] No selection or clipboardData');
      }
      pendingClipboardAction.current = null;
    } catch (err) {
      console.error('📋 [ClipboardBridge] Copy error:', err);
    }
  }, [getSelectedText]);

  const handleCut = React.useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    DEBUG_ENABLED && console.log('📋 [ClipboardBridge] handleCut fired');
    try {
      const selected = getSelectedText();
      DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Selected text for cut:', { length: selected?.length, preview: selected?.slice(0, 50) });
      
      if (selected && e.clipboardData) {
        e.clipboardData.setData('text/plain', selected);
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Set clipboard data for cut:', selected.length, 'chars');
        e.preventDefault();
        onCutSelection?.();
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Called onCutSelection');
      } else {
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] No selection or clipboardData for cut');
      }
      pendingClipboardAction.current = null;
    } catch (err) {
      console.error('📋 [ClipboardBridge] Cut error:', err);
    }
  }, [getSelectedText, onCutSelection]);

  // ✅ CRITICAL: Intercept Ctrl+C/V/X at window level, focus this element,
  // then let the browser fire native clipboard events on it
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      if (!ctrlOrMeta) return;
      
      const key = e.key.toLowerCase();
      if (key === 'c' || key === 'v' || key === 'x') {
        DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Intercepted Ctrl+' + key.toUpperCase());
        
        // Focus the contenteditable div so clipboard events fire on it
        if (innerRef.current) {
          pendingClipboardAction.current = key === 'c' ? 'copy' : key === 'v' ? 'paste' : 'cut';
          innerRef.current.focus();
          DEBUG_ENABLED && console.log('📋 [ClipboardBridge] Focused bridge, pending action:', pendingClipboardAction.current);
          
          // For paste, we need to let the browser handle it
          // For copy/cut, we also let browser handle it - the native event will fire
          // and our handleCopy/handleCut will intercept it
        }
        // DON'T prevent default - let the browser fire the native clipboard event
      }
    };
    
    window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  return (
    <div
      ref={innerRef}
      contentEditable
      aria-hidden
      onPaste={handlePaste}
      onCopy={handleCopy}
      onCut={handleCut}
      // Off-screen and visually hidden but still focusable
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        overflow: 'hidden',
        left: -9999,
        top: 0,
      }}
      tabIndex={-1}
      suppressContentEditableWarning
    />
  );
});

export default ClipboardBridge;


